import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { HelpCircle, Loader2, Clock, Store, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePageT } from '../i18n/usePageT';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductQnA = ({ productId }) => {
  const { isAuthenticated, token } = useAuth();
  const T = usePageT();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchQuestions = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const r = await axios.get(`${API}/products/${productId}/questions`, { headers });
      setQuestions(r.data || []);
    } catch {
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [productId, token]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const submit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.info(T('Connectez-vous pour poser une question', 'Sign in to ask a question'));
      navigate('/login');
      return;
    }
    if (text.trim().length < 5) {
      toast.error(T('Votre question est trop courte (5 caractères minimum)', 'Your question is too short (min. 5 characters)'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/products/${productId}/questions`,
        { question: text.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(T('Question envoyée au vendeur. Elle sera publiée dès sa réponse.', 'Question sent to the seller. It will be published once answered.'));
      setText('');
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur lors de l\'envoi', 'Error while sending'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="qna-section">
      <form onSubmit={submit} className="bg-[#FBF8F3] border border-[#0F4C5C]/10 rounded-2xl p-5 mb-8">
        <p className="font-semibold text-[#0F4C5C] flex items-center gap-2 mb-3">
          <HelpCircle className="w-5 h-5" />
          {T('Une question sur ce produit ?', 'Have a question about this product?')}
        </p>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={1000}
          rows={3}
          placeholder={T('Posez votre question au vendeur…', 'Ask the seller your question…')}
          data-testid="qna-ask-input"
        />
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-muted-foreground">
            {T('Votre question sera publiée sur cette fiche après la réponse du vendeur.', 'Your question will be published on this page after the seller answers.')}
          </p>
          <Button type="submit" disabled={submitting} className="btn-primary" data-testid="qna-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {T('Envoyer', 'Send')}
          </Button>
        </div>
      </form>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#0F4C5C]" /></div>
      ) : questions.length === 0 ? (
        <p className="text-muted-foreground text-sm" data-testid="qna-empty">
          {T('Aucune question pour le moment. Soyez le premier à en poser une !', 'No questions yet. Be the first to ask one!')}
        </p>
      ) : (
        <div className="space-y-5">
          {questions.map((q) => (
            <div key={q.id} className="border border-[#0F4C5C]/10 rounded-2xl p-5 bg-white" data-testid={`qna-item-${q.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#0F4C5C]">
                    <span className="text-[#E9C46A] mr-1.5">Q.</span>
                    {q.question}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {q.user_name} · {new Date(q.created_at).toLocaleDateString()}
                  </p>
                </div>
                {q.status === 'pending' && (
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 whitespace-nowrap" data-testid={`qna-pending-badge-${q.id}`}>
                    <Clock className="w-3 h-3 mr-1" />
                    {T('En attente de réponse', 'Awaiting answer')}
                  </Badge>
                )}
              </div>
              {q.answer && (
                <div className="mt-4 ml-3 pl-4 border-l-2 border-[#0F4C5C]/30 bg-[#FBF8F3] rounded-r-xl py-3 pr-4" data-testid={`qna-answer-${q.id}`}>
                  <p className="text-xs font-semibold text-[#0F4C5C] flex items-center gap-1.5 mb-1">
                    <Store className="w-3.5 h-3.5" />
                    {q.answer.vendor_name} · {new Date(q.answer.created_at).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-[#0F4C5C]/90">{q.answer.content}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
